"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    investors: string[];
    fund: string;
    oneBasket: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { equityA: "株式A", equityB: "株式B", govBond: "国債", corpBond: "社債", reit: "REIT", gold: "金" },
    investors: ["投資家A", "投資家B", "投資家C"],
    fund: "投資信託",
    oneBasket: "1本でさまざまな資産をまとめて持てる",
    title: "投資信託は『まとめて持つバスケット』",
    description: "投資家から集めたお金を、株式・債券・REIT・コモディティなどにまとめて運用する商品です。",
  },
  en: {
    assets: {
      equityA: "Equity A",
      equityB: "Equity B",
      govBond: "Gov. bonds",
      corpBond: "Corp. bonds",
      reit: "REIT",
      gold: "Gold",
    },
    investors: ["Investor A", "Investor B", "Investor C"],
    fund: "Mutual fund",
    oneBasket: "One product holds a range of assets together",
    title: "A mutual fund is a basket you hold in one go",
    description:
      "It pools money from investors and runs it across equities, bonds, REITs, commodities, and more.",
  },
};

// ── Asset colors (aligned with chapter 4 palette) ──────────────────

const ASSET_COLOR = {
  equity: COLOR.sky,
  bond: COLOR.amber,
  reit: "#ec4899",
  commodity: COLOR.emerald,
};

const PERSONA_COLOR = "#64748b";

// ── Assets inside the fund ─────────────────────────────────────────

type Asset = { key: string; color: string; weight: number };

const ASSETS: Asset[] = [
  { key: "equityA", color: ASSET_COLOR.equity, weight: 30 },
  { key: "equityB", color: ASSET_COLOR.equity, weight: 20 },
  { key: "govBond", color: ASSET_COLOR.bond, weight: 20 },
  { key: "corpBond", color: ASSET_COLOR.bond, weight: 10 },
  { key: "reit", color: ASSET_COLOR.reit, weight: 10 },
  { key: "gold", color: ASSET_COLOR.commodity, weight: 10 },
];

// ── Circle packing (same algorithm as chapter 4) ───────────────────

type Circle = { x: number; y: number; r: number; asset: Asset };

function packCircles(
  assets: Asset[],
  cx: number,
  cy: number,
  areaW: number,
  areaH: number,
  minR: number,
  maxR: number,
): Circle[] {
  const maxWeight = Math.max(...assets.map((a) => a.weight));
  const circles: Circle[] = assets
    .map((asset) => {
      const frac = asset.weight / maxWeight;
      const r = minR + (maxR - minR) * frac;
      return { x: cx, y: cy, r, asset };
    })
    .sort((a, b) => b.r - a.r);

  const angleStep = (Math.PI * 2) / circles.length;
  circles.forEach((c, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const spread = areaW * 0.18;
    c.x = cx + Math.cos(angle) * spread;
    c.y = cy + Math.sin(angle) * spread;
  });

  const halfW = areaW / 2;
  const halfH = areaH / 2;
  for (let iter = 0; iter < 80; iter++) {
    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        const a = circles[i];
        const b = circles[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
        const minDist = a.r + b.r + 3;
        if (dist < minDist) {
          const push = ((minDist - dist) / dist) * 0.4;
          const px = dx * push;
          const py = dy * push;
          a.x -= px;
          a.y -= py;
          b.x += px;
          b.y += py;
        }
      }
    }

    for (const c of circles) {
      c.x += (cx - c.x) * 0.06;
      c.y += (cy - c.y) * 0.06;
    }

    for (const c of circles) {
      const yRatio = (c.y - (cy - halfH)) / areaH;
      const taperFactor = 1 - yRatio * 0.1;
      const localHalfW = halfW * taperFactor;

      if (c.x - c.r < cx - localHalfW) c.x = cx - localHalfW + c.r;
      if (c.x + c.r > cx + localHalfW) c.x = cx + localHalfW - c.r;
      if (c.y - c.r < cy - halfH) c.y = cy - halfH + c.r;
      if (c.y + c.r > cy + halfH) c.y = cy + halfH - c.r;
    }
  }

  return circles;
}

// ── SVG primitives ─────────────────────────────────────────────────

function Basket({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const r = 10;
  const rimH = 7;
  return (
    <g>
      <rect
        x={x - 3}
        y={y - rimH}
        width={w + 6}
        height={rimH}
        rx={3}
        fill="currentColor"
        fillOpacity={0.08}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.18}
      />
      <path
        d={`M ${x} ${y}
            L ${x + 6} ${y + h - r} Q ${x + 6} ${y + h} ${x + 6 + r} ${y + h}
            L ${x + w - 6 - r} ${y + h} Q ${x + w - 6} ${y + h} ${x + w - 6} ${y + h - r}
            L ${x + w} ${y} Z`}
        fill="currentColor"
        fillOpacity={0.02}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.14}
      />
      {[0.3, 0.55, 0.8].map((f) => {
        const ly = y + f * h;
        const inset = 6 + f * 5;
        return (
          <line
            key={f}
            x1={x + inset}
            y1={ly}
            x2={x + w - inset}
            y2={ly}
            stroke="currentColor"
            strokeWidth="0.5"
            strokeOpacity={0.06}
          />
        );
      })}
    </g>
  );
}

function AssetBubble({ cx, cy, r, asset }: { cx: number; cy: number; r: number; asset: Asset }) {
  const t = STRINGS[useDocLocale()];
  const color = asset.color;
  const isSmall = r < 22;

  if (isSmall) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.14} stroke={color} strokeWidth="1.2" />
        <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central" fontSize={8} fill={color} fontWeight="700">
          {t.assets[asset.key]}
        </text>
      </g>
    );
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.14} stroke={color} strokeWidth="1.2" />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central" fontSize={r > 30 ? 10 : 9} fill={color} fontWeight="600">
        {t.assets[asset.key]}
      </text>
    </g>
  );
}

function PersonIcon({ cx, cy, size }: { cx: number; cy: number; size: number }) {
  const headR = size * 0.28;
  const bodyW = size * 0.6;
  const bodyH = size * 0.35;
  const headY = cy - size * 0.18;
  const bodyY = headY + headR + size * 0.06;
  return (
    <g>
      <circle cx={cx} cy={headY} r={headR} fill={PERSONA_COLOR} />
      <rect x={cx - bodyW / 2} y={bodyY} width={bodyW} height={bodyH} rx={bodyW / 2} fill={PERSONA_COLOR} />
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function FundBasketIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 300;

  // Investors (left)
  const invX = 50;
  const investors = [
    { label: t.investors[0]!, y: 70 },
    { label: t.investors[1]!, y: 150 },
    { label: t.investors[2]!, y: 230 },
  ];

  // Basket (right)
  const basketX = 200;
  const basketY = 50;
  const basketW = 220;
  const basketH = 210;
  const basketCx = basketX + basketW / 2;
  const basketCy = basketY + basketH / 2;

  const circles = packCircles(ASSETS, basketCx, basketCy, basketW - 24, basketH - 28, 14, 40);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Investors */}
      {investors.map(({ label, y }) => (
        <g key={label}>
          <PersonIcon cx={invX} cy={y} size={26} />
          <text x={invX} y={y + 22} textAnchor="middle" fontSize="8.5" fill={PERSONA_COLOR} fontWeight="600">
            {label}
          </text>
        </g>
      ))}

      {/* Arrows: investors → basket */}
      {investors.map(({ label, y }) => {
        const startX = invX + 24;
        const endX = basketX - 4;
        const endY = basketCy;
        const midX = (startX + endX) / 2;
        return (
          <g key={`arrow-${label}`}>
            <path
              d={`M ${startX} ${y} C ${midX} ${y}, ${midX} ${endY}, ${endX} ${endY}`}
              fill="none"
              stroke={PERSONA_COLOR}
              strokeWidth="1"
              strokeDasharray="4 4"
            />
          </g>
        );
      })}
      {/* Arrowhead */}
      <path
        d={`M ${basketX - 10} ${basketCy - 4} L ${basketX - 2} ${basketCy} L ${basketX - 10} ${basketCy + 4}`}
        fill="none"
        stroke={PERSONA_COLOR}
        strokeWidth="1.2"
      />

      {/* Basket label */}
      <text x={basketCx} y={basketY - 16} textAnchor="middle" fontSize="12" fill="currentColor" fontWeight="700">
        {t.fund}
      </text>

      {/* Basket */}
      <Basket x={basketX} y={basketY} w={basketW} h={basketH} />

      {/* Asset bubbles */}
      {circles.map((c) => (
        <AssetBubble key={c.asset.key} cx={c.x} cy={c.y} r={c.r} asset={c.asset} />
      ))}

      {/* Caption */}
      <text x={basketCx} y={basketY + basketH + 20} textAnchor="middle" fontSize="9" fill="currentColor">
        {t.oneBasket}
      </text>
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocFundStructureDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <FundBasketIllustration />
    </IllustrationCard>
  );
}
